import React, { useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Avatar } from '@/components/ui/Avatar'
import { Relationship } from '@/types/matching'
import StarRating from './StarRating'

interface RateRelationshipModalProps {
  relationship: Relationship
  userRole: 'mentor' | 'mentee'
  isOpen: boolean
  onClose: () => void
  onSubmit: (rating: number, review?: string) => Promise<void>
  isSubmitting?: boolean
}

const RateRelationshipModal: React.FC<RateRelationshipModalProps> = ({
  relationship,
  userRole,
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false
}) => {
  const [rating, setRating] = useState(0)
  const [review, setReview] = useState('')
  const [error, setError] = useState('')

  if (!isOpen) return null

  const otherPerson = userRole === 'mentor' ? relationship.mentee : relationship.mentor
  const roleLabel = userRole === 'mentor' ? 'mentee' : 'mentor'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (rating < 1 || rating > 5) {
      setError('Please select a star rating')
      return
    }

    await onSubmit(rating, review.trim() ? review.trim() : undefined)
    setRating(0)
    setReview('')
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setRating(0)
      setReview('')
      setError('')
      onClose()
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          {/* Close button */}
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Rate Your {roleLabel === 'mentee' ? 'Mentee' : 'Mentor'}</h2>
            {otherPerson && (
              <p className="mt-1 text-sm text-gray-600">
                How was your mentorship with {otherPerson.firstName} {otherPerson.lastName}?
              </p>
            )}
          </div>

          {/* Other person info */}
          {otherPerson && (
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg mb-6">
              <Avatar
                src={otherPerson.profile?.profilePicture}
                name={`${otherPerson.firstName} ${otherPerson.lastName}`}
                size="md"
              />
              <p className="font-medium text-gray-900">
                {otherPerson.firstName} {otherPerson.lastName}
              </p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="mb-4 flex flex-col items-center">
              <StarRating value={rating} onChange={setRating} size="lg" />
              {error && <span className="text-xs text-red-500 mt-2">{error}</span>}
            </div>

            <div className="mb-4">
              <label htmlFor="review" className="block text-sm font-medium text-gray-700 mb-2">
                Written Review (optional)
              </label>
              <textarea
                id="review"
                value={review}
                onChange={(e) => setReview(e.target.value)}
                placeholder="Share more about your experience..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                rows={4}
                disabled={isSubmitting}
                maxLength={1000}
              />
              <div className="flex justify-end mt-1">
                <span className="text-xs text-gray-400">{review.length}/1000</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 justify-end">
              <Button type="button" variant="secondary" onClick={handleClose} disabled={isSubmitting}>
                Maybe Later
              </Button>
              <Button type="submit" variant="primary" disabled={isSubmitting || rating < 1} isLoading={isSubmitting}>
                Submit Rating
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default RateRelationshipModal
